import React, { BaseSyntheticEvent } from 'react'
import { IonList, IonItem, IonCol, IonRow, IonLabel, IonIcon } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router'
import './AddPatientInformation.component.css'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedOption, enableScroll } from '../store/patientInformation.actions'
import { PatientInformationConstants } from '../store/patientInformation.constants'
import { getPatientInformationState } from '../selectors/patientInformation.selector'
import { setShowSingleSelectLookup } from '../../patientMedications/store/patientMedications.actions'
import { getPatientSearchData } from '../../patientSearch/store/patientSearch.actions'
import { hasUnsavedPhysiciansData, showUnsavedDataWarningMessage } from '../../../utilities/unSavedData.util'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { IPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.types'
import {
  clearPhysicianData,
  savePatientDemographics,
} from '../../patientDemographics/store/patientDemographics.actions'

const AddPatientInformation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { selectedOption, hidePatientAndBilling, searchImport } = useSelector(getPatientInformationState)
  const patientDemographics: IPatientDemographicsState = useSelector(getPatientDemographicsSelector)
  const { isTablet } = useDeviceMetrics()

  const runUnsavedDataCheck = (onConfirmed: () => void) => {
    if (selectedOption === PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION) {
      if (hasUnsavedPhysiciansData(patientDemographics)) {
        showUnsavedDataWarningMessage(dispatch, t, t('patientDemographics.unsavedDataWarningMessage'), () => {
          dispatch(clearPhysicianData())
          dispatch(savePatientDemographics())
          onConfirmed()
        })
        return
      }
    }
    onConfirmed()
  }

  const navigateToPatientDemographics = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION))
    } else {
      history.push(TabConstants.PATIENT_DEMOGRAPHICS)
    }
    dispatch(enableScroll(false))
  }

  const navigateToPatientAllergies = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runUnsavedDataCheck(() => {
        dispatch(setSelectedOption(PatientInformationConstants.PATIENT_ALLERGIES_OPTION))
      })
    } else {
      history.push(TabConstants.PATIENT_ALLERGIES)
    }
    dispatch(enableScroll(false))
  }

  const navigateToPatientMedicalHistory = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runUnsavedDataCheck(() => {
        dispatch(setSelectedOption(PatientInformationConstants.PATIENT_MEDICAL_HISTORY_OPTION))
      })
    } else {
      history.push(TabConstants.PATIENT_MEDICAL_HISTORY)
    }
    dispatch(enableScroll(false))
  }

  const navigateToPatientMedications = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runUnsavedDataCheck(() => {
        dispatch(setSelectedOption(PatientInformationConstants.PATIENT_MEDICATION_OPTION))
        dispatch(setShowSingleSelectLookup(false))
      })
    } else {
      history.push(TabConstants.PATIENT_MEDICATIONS)
    }
    dispatch(enableScroll(false))
  }

  const navigateToPatientSearch = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runUnsavedDataCheck(() => {
        dispatch(getPatientSearchData())
        dispatch(setShowSingleSelectLookup(false))
        dispatch(setSelectedOption(PatientInformationConstants.PATIENT_SEARCH_OPTION))
      })
    }
    dispatch(enableScroll(false))
  }

  const isRowSelected = (rowName: string): boolean => {
    return isTablet && selectedOption === rowName
  }

  const isPatientSearch = () => {
    return (
      isRowSelected(PatientInformationConstants.PATIENT_SEARCH_OPTION) ||
      isRowSelected(PatientInformationConstants.PATIENT_SEARCH_RESULTS_OPTION)
    )
  }
  return (
    <IonList>
      {isTablet && !searchImport && (
        <IonItem
          data-testid='PatientSearch'
          onClick={navigateToPatientSearch}
          className={isPatientSearch() ? 'aqua-base' : ''}>
          <IonCol className='ion-padding'>
            <IonRow>
              <IonIcon
                className={isPatientSearch() ? 'aqua-base-icon icon-padding-right' : 'icon-padding-right'}
                src='/assets/images/folder-magnifying-glass.svg'
                size='medium'
              />
              <IonLabel color='dark'>
                <h2>{t('patientSearch.patientSearch')}</h2>
              </IonLabel>
            </IonRow>
          </IonCol>
        </IonItem>
      )}
      {!hidePatientAndBilling && (
        <>
          <IonItem
            data-testid='PatientDemographics'
            onClick={navigateToPatientDemographics}
            className={
              isRowSelected(PatientInformationConstants.SET_DO_NOT_RESUSCITATE) ||
              isRowSelected(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION)
                ? 'aqua-base'
                : ''
            }>
            <IonCol className='ion-padding'>
              <IonRow>
                <IonLabel color='dark'>
                  <h2>{t('fullChart.patient.patientInformation.PatientDemographics')}</h2>
                </IonLabel>
              </IonRow>
              <IonRow>
                <IonLabel color='medium'>
                  <h3>{t('fullChart.patient.patientInformation.IDDemographicsAddress')}</h3>
                </IonLabel>
              </IonRow>
            </IonCol>
          </IonItem>
          <IonItem
            data-testid='PatientMedicalHistory'
            className={
              isRowSelected(PatientInformationConstants.PATIENT_MEDICAL_HISTORY_OPTION) ||
              isRowSelected(PatientInformationConstants.PATIENT_MEDICAL_HISTORY_SEARCH_LIST_OPTION)
                ? 'aqua-base'
                : ''
            }
            onClick={navigateToPatientMedicalHistory}>
            <IonCol className='ion-padding'>
              <IonRow>
                <IonLabel color='dark'>
                  <h2>{t('fullChart.patient.patientInformation.PatientMedicalHistory')}</h2>
                </IonLabel>
              </IonRow>
              <IonRow>
                <IonLabel color='medium'>
                  <h3>{t('fullChart.patient.patientInformation.PastAndCurrentMedicalConditions')}</h3>
                </IonLabel>
              </IonRow>
            </IonCol>
          </IonItem>
          <IonItem
            data-testid='PatientAllergies'
            className={
              isRowSelected(PatientInformationConstants.PATIENT_ALLERGIES_OPTION) ||
              isRowSelected(PatientInformationConstants.PATIENT_ALLERGIES_SEARCH_LIST_OPTION)
                ? 'aqua-base'
                : ''
            }
            onClick={navigateToPatientAllergies}>
            <IonCol className='ion-padding'>
              <IonRow>
                <IonLabel color='dark'>
                  <h2>{t('fullChart.patient.patientInformation.PatientAllergies')}</h2>
                </IonLabel>
              </IonRow>
              <IonRow>
                <IonLabel color='medium'>
                  <h3>{t('fullChart.patient.patientInformation.AllergiesToMedicationEnvironmental')}</h3>
                </IonLabel>
              </IonRow>
            </IonCol>
          </IonItem>
          <IonItem
            data-testid='PatientMedications'
            onClick={navigateToPatientMedications}
            className={isRowSelected(PatientInformationConstants.PATIENT_MEDICATION_OPTION) ? 'aqua-base' : ''}>
            <IonCol className='ion-padding'>
              <IonRow>
                <IonLabel color='dark'>
                  <h2>{t('fullChart.patient.patientInformation.PatientMedication')}</h2>
                </IonLabel>
              </IonRow>
              <IonRow>
                <IonLabel color='medium'>
                  <h3>{t('fullChart.patient.patientInformation.CurrentPrescribedMedications')}</h3>
                </IonLabel>
              </IonRow>
            </IonCol>
          </IonItem>
        </>
      )}
    </IonList>
  )
}

export default AddPatientInformation
